import * as React from 'react';

type TextareaValue = React.TextareaHTMLAttributes<HTMLTextAreaElement>['value'];
type TextareaDefaultValue =
  React.TextareaHTMLAttributes<HTMLTextAreaElement>['defaultValue'];

const isEmptyValue = (value: TextareaValue | TextareaDefaultValue) => {
  if (value === undefined || value === null) return true;
  return String(value).length === 0;
};

const getTextareaEmptyState = ({
  empty,
  defaultValue,
  value,
}: {
  empty?: boolean;
  value?: TextareaValue;
  defaultValue?: TextareaDefaultValue;
}) => {
  if (typeof empty !== 'undefined') return empty;
  if (typeof value !== 'undefined') return isEmptyValue(value);
  return isEmptyValue(defaultValue);
};

export function useTextareaValueState({
  empty,
  value,
  defaultValue,
}: {
  empty?: boolean;
  value?: TextareaValue;
  defaultValue?: TextareaDefaultValue;
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const isControlled = typeof empty !== 'undefined' || typeof value !== 'undefined';
  const propsAreEmpty = getTextareaEmptyState({empty, value, defaultValue});
  const [nativeIsEmpty, setNativeIsEmpty] = React.useState(propsAreEmpty);
  const isEmpty = isControlled ? propsAreEmpty : nativeIsEmpty;

  const setNativeValue = React.useCallback(
    (nextValue: string) => {
      if (isControlled) return;

      const nextIsEmpty = nextValue.length === 0;
      setNativeIsEmpty(prevIsEmpty =>
        prevIsEmpty === nextIsEmpty ? prevIsEmpty : nextIsEmpty,
      );
    },
    [isControlled],
  );

  const syncValueState = React.useCallback(() => {
    if (!textareaRef.current) return;
    setNativeValue(textareaRef.current.value);
  }, [setNativeValue]);

  // Form libraries can preload uncontrolled values through refs without props.
  React.useLayoutEffect(() => {
    syncValueState();
  });

  React.useEffect(() => {
    const textarea = textareaRef.current;
    const form = textarea?.form;

    if (isControlled || !form) return;

    const handleReset = () => {
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(syncValueState);
        return;
      }

      globalThis.setTimeout(syncValueState, 0);
    };

    form.addEventListener('reset', handleReset);
    return () => form.removeEventListener('reset', handleReset);
  }, [isControlled, syncValueState]);

  return {
    textareaRef,
    isControlled,
    isEmpty,
    setNativeValue,
    syncValueState,
  };
}
